import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House,
  Factory,
  Lightning,
  Package,
  ClipboardText,
  Handshake,
  ChartBar,
  Users,
  SignOut,
  CircleDashed,
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
  { to: "/production", label: "Production", icon: Factory, testid: "nav-production" },
  { to: "/electricity", label: "Electricity", icon: Lightning, testid: "nav-electricity" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/sauda", label: "Sauda", icon: Handshake, testid: "nav-sauda" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports" },
  { to: "/users", label: "User Access", icon: Users, testid: "nav-users", adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [now] = useState(new Date());

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-800 flex-shrink-0 flex flex-col bg-[#0b0b0d]">
        <div className="p-5 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="h-8 w-8 bg-amber-400 grid place-items-center font-display font-black text-black">D</div>
            <div>
              <div className="font-display font-black text-sm tracking-tight">DIGITEX / OPS</div>
              <div className="label-caps text-[9px]">Command Center</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            if (item.adminOnly && user?.role !== "admin") return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-zinc-900 text-amber-400 border-l-2 border-amber-400"
                      : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={16} weight="bold" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8" />
            ) : (
              <div className="h-8 w-8 bg-zinc-800 grid place-items-center text-xs font-semibold">
                {user?.name?.[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="label-caps text-[9px] truncate" data-testid="current-user-role">
                {user?.role}
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-zinc-500 hover:text-amber-400 transition-colors"
              aria-label="Sign out"
            >
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-6 bg-[#0b0b0d]">
          <div className="flex items-center gap-4 text-xs font-mono text-zinc-500">
            <CircleDashed size={12} className="text-emerald-400 animate-spin" style={{ animationDuration: "6s" }} />
            <span className="label-caps text-[10px]">LIVE</span>
            <span className="w-px h-4 bg-zinc-800" />
            <span>{now.toDateString().toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <ClipboardText size={14} className="text-zinc-500" />
            <span className="label-caps text-[10px]">Assigned units:</span>
            <span className="font-mono text-amber-400" data-testid="assigned-units">
              {user?.role === "admin" ? "ALL" : (user?.assigned_units || []).join(" · ") || "—"}
            </span>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6 animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
